import { Decimal } from 'decimal.js';
import { roundRate } from './salePriceRuleUtils';

export interface RateCalculationResult {
  rate: number;
  taxableAmount: number;
  taxAmount: number;
  discountAmount: number;
  isValid: boolean;
  error?: string;
}

/**
 * Calculate rate from total amount for purchase invoices
 * This performs a reverse calculation: given a total amount, calculate what the rate should be
 * 
 * @param totalAmount - The desired total amount (including tax)
 * @param quantity - The quantity of items
 * @param taxRatePercent - The tax rate percentage (e.g., 18 for 18%)
 * @param discountPercent - Discount percentage (if any)
 * @param discountAmount - Discount amount (if any) - takes precedence over percentage
 * @param isTaxInclusive - Whether the rate includes tax
 * @param rateDecimalPlaces - Number of decimal places for rate rounding
 * @returns Calculated rate and breakdown
 */
export function calculateRateFromTotalAmount(
  totalAmount: number | string,
  quantity: number | string,
  taxRatePercent: number = 0,
  discountPercent: number = 0,
  discountAmount: number = 0,
  isTaxInclusive: boolean = false,
  rateDecimalPlaces: number = 0
): RateCalculationResult {
  console.log('[calculateRateFromTotalAmount] Input params:', {
    totalAmount,
    quantity,
    taxRatePercent,
    discountPercent,
    discountAmount,
    isTaxInclusive,
    rateDecimalPlaces
  });

  try {
    // Parse inputs
    const total = new Decimal(totalAmount);
    const qty = new Decimal(quantity);
    const taxRate = new Decimal(taxRatePercent).div(100);
    const discPct = new Decimal(discountPercent).div(100);
    const discAmt = new Decimal(discountAmount);

    // Validate inputs
    if (qty.lte(0)) {
      return {
        rate: 0,
        taxableAmount: 0,
        taxAmount: 0,
        discountAmount: 0,
        isValid: false,
        error: 'Quantity must be greater than zero'
      };
    }

    if (total.lt(0)) {
      return {
        rate: 0,
        taxableAmount: 0,
        taxAmount: 0,
        discountAmount: 0,
        isValid: false,
        error: 'Total amount cannot be negative'
      };
    }

    let calculatedRate: Decimal;
    let taxableAmount: Decimal;
    let taxAmount: Decimal;
    let actualDiscountAmount: Decimal;

    if (isTaxInclusive) {
      // For tax-inclusive pricing:
      // The total amount entered is the final amount including tax
      // rate = totalAmount / quantity (where rate includes tax)
      
      // First, calculate the tax-inclusive rate considering discounts
      if (discAmt.gt(0)) {
        // With fixed discount: totalAmount = (qty * rate) - discountAmount
        // So: rate = (totalAmount + discountAmount) / qty
        const grossAmount = total.add(discAmt);
        calculatedRate = grossAmount.div(qty);
        actualDiscountAmount = discAmt;
      } else if (discPct.gt(0)) {
        // With percentage discount: totalAmount = (qty * rate) * (1 - discountPercent)
        // So: rate = totalAmount / (qty * (1 - discountPercent))
        const discountMultiplier = new Decimal(1).sub(discPct);
        calculatedRate = total.div(qty.mul(discountMultiplier));
        actualDiscountAmount = calculatedRate.mul(qty).mul(discPct);
      } else {
        // No discount: simple division
        calculatedRate = total.div(qty);
        actualDiscountAmount = new Decimal(0);
      }

      // For tax-inclusive, the rate already includes tax
      // We need to extract the tax component from the total
      // totalAmount = taxableAmount + taxAmount
      // But since rate is tax-inclusive: taxableAmount = totalAmount / (1 + taxRate)
      taxableAmount = total.div(new Decimal(1).add(taxRate));
      taxAmount = total.sub(taxableAmount);
    } else {
      // For tax-exclusive pricing:
      // totalAmount = taxableAmount + taxAmount
      // taxableAmount = (quantity * rate) - discount
      // taxAmount = taxableAmount * taxRate
      // So: totalAmount = taxableAmount * (1 + taxRate)
      // Therefore: taxableAmount = totalAmount / (1 + taxRate)

      taxableAmount = total.div(new Decimal(1).add(taxRate));
      taxAmount = total.sub(taxableAmount);

      // Now calculate rate from taxableAmount
      // taxableAmount = (qty * rate) - discount
      if (discAmt.gt(0)) {
        // Fixed discount: taxableAmount = (qty * rate) - discountAmount
        // So: rate = (taxableAmount + discountAmount) / qty
        const grossAmount = taxableAmount.add(discAmt);
        calculatedRate = grossAmount.div(qty);
        actualDiscountAmount = discAmt;
      } else if (discPct.gt(0)) {
        // Percentage discount: taxableAmount = (qty * rate) * (1 - discountPercent)
        // So: rate = taxableAmount / (qty * (1 - discountPercent))
        const discountMultiplier = new Decimal(1).sub(discPct);
        calculatedRate = taxableAmount.div(qty.mul(discountMultiplier));
        const grossAmount = calculatedRate.mul(qty);
        actualDiscountAmount = grossAmount.mul(discPct);
      } else {
        // No discount: taxableAmount = qty * rate
        calculatedRate = taxableAmount.div(qty);
        actualDiscountAmount = new Decimal(0);
      }
    }

    // Round the rate according to decimal places configuration
    const roundedRate = roundRate(calculatedRate, rateDecimalPlaces);

    // Recalculate final values with rounded rate to ensure consistency
    const finalGrossAmount = qty.mul(roundedRate);
    
    if (discAmt.gt(0)) {
      actualDiscountAmount = discAmt;
    } else if (discPct.gt(0)) {
      actualDiscountAmount = finalGrossAmount.mul(discPct);
    }

    const finalNetAmount = finalGrossAmount.sub(actualDiscountAmount);
    
    if (isTaxInclusive) {
      // For tax-inclusive: the finalNetAmount includes tax
      // Extract tax: taxableAmount = finalNetAmount / (1 + taxRate)
      taxableAmount = finalNetAmount.div(new Decimal(1).add(taxRate));
      taxAmount = finalNetAmount.sub(taxableAmount);
    } else {
      // For tax-exclusive: finalNetAmount is the taxable amount
      taxableAmount = finalNetAmount;
      taxAmount = finalNetAmount.mul(taxRate);
    }

    const result = {
      rate: roundedRate.toNumber(),
      taxableAmount: taxableAmount.toNumber(),
      taxAmount: taxAmount.toNumber(),
      discountAmount: actualDiscountAmount.toNumber(),
      isValid: true
    };

    console.log('[calculateRateFromTotalAmount] Result:', {
      ...result,
      isTaxInclusive
    });

    return result;
  } catch (error) {
    console.error('[purchaseInvoiceCalculations] Error calculating rate from amount:', error);
    return {
      rate: 0,
      taxableAmount: 0,
      taxAmount: 0,
      discountAmount: 0,
      isValid: false,
      error: 'Calculation error occurred'
    };
  }
}

/**
 * Validate if the calculated rate will produce the expected total
 * This helps ensure our reverse calculation is accurate
 */
export function validateRateCalculation(
  rate: number,
  quantity: number,
  taxRatePercent: number,
  discountPercent: number,
  discountAmount: number,
  isTaxInclusive: boolean,
  expectedTotal: number,
  tolerance: number = 0.01
): boolean {
  const qty = new Decimal(quantity);
  const r = new Decimal(rate);
  const taxRate = new Decimal(taxRatePercent).div(100);
  const discPct = new Decimal(discountPercent).div(100);
  const discAmt = new Decimal(discountAmount);

  const grossAmount = qty.mul(r);
  
  let actualDiscountAmount: Decimal;
  if (discAmt.gt(0)) {
    actualDiscountAmount = discAmt;
  } else if (discPct.gt(0)) {
    actualDiscountAmount = grossAmount.mul(discPct);
  } else {
    actualDiscountAmount = new Decimal(0);
  }

  const netAmount = grossAmount.sub(actualDiscountAmount);
  
  let total: Decimal;
  if (isTaxInclusive) {
    total = netAmount;
  } else {
    const taxAmount = netAmount.mul(taxRate);
    total = netAmount.add(taxAmount);
  }

  const difference = total.sub(expectedTotal).abs();
  return difference.lte(tolerance);
}